import React, { useState, useEffect, useCallback } from 'react';
import {
  Steps, Tag, Typography, Space, Button, Modal, Select, Input,
  message, Tooltip, Alert, Card, Descriptions, Upload, Collapse,
  Tabs, Table, Spin,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SwapOutlined,
  CheckOutlined,
  StopOutlined,
  LockOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  UserOutlined,
  PaperClipOutlined,
  UploadOutlined,
  ArrowRightOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  DownloadOutlined,
  LinkOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import type { ReviewGateDto, ReviewStatus } from '@/types/review';
import type { User } from '@/types/common';
import { reviewApi } from '@/api/reviewApi';
import { userApi } from '@/api/userApi';
import { documentApi, type DocumentDto } from '@/api/documentApi';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ProposalAIReview from './ProposalAIReview';
import { workflowApi } from '@/api/workflowApi';
import { useAuth } from '@/auth/useAuth';
import ComplianceMatrixForm from '@/components/workflow/ComplianceMatrixForm';

const { Text } = Typography;
const { TextArea } = Input;

interface ReviewSubGatesProps {
  reviewGates: ReviewGateDto[];
  opportunityId: string;
  onGatesUpdated?: () => void;
  onRollback?: () => void; // called after a successful rollback so parent can refresh
}

const GATE_TYPE_LABELS: Record<string, string> = {
  TECHNICAL: 'E.1 Technical Review',
  STRATEGY_DELIVERY: 'E.2 Strategy & Delivery Review',
  COMPLIANCE: 'E.3 Compliance Review',
  EXECUTIVE: 'E.4 Executive Review & Signature',
};

/** Role(s) that can approve/reject each gate type. */
const GATE_ROLE_PERMISSIONS: Record<string, string[]> = {
  TECHNICAL: ['CLOUD_ARCHITECT', 'ADMIN', 'CEO'],
  STRATEGY_DELIVERY: ['BD_MANAGER', 'ADMIN', 'CEO'],
  COMPLIANCE: ['QC_LEAD', 'ADMIN', 'CEO'],
  EXECUTIVE: ['CEO', 'ADMIN'],
};

/** Human-readable label for each role. */
const ROLE_LABELS: Record<string, string> = {
  CLOUD_ARCHITECT: 'Cloud Architect',
  BD_MANAGER: 'BD Manager',
  QC_LEAD: 'QC Lead',
  CEO: 'CEO',
  ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  ADMIN: 'Admin',
  TECHNICAL_WRITER: 'Technical Writer',
  OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager',
};

/** RACI info for each gate (for "not initialized" summary + modal). */
const GATE_DESCRIPTIONS: Record<string, { role: string; description: string }> = {
  TECHNICAL: {
    role: 'Cloud Architect',
    description: 'Technical feasibility, architecture soundness, and solution quality review.',
  },
  STRATEGY_DELIVERY: {
    role: 'BD Manager',
    description: 'Delivery strategy, timeline, pricing, and win-theme alignment review.',
  },
  COMPLIANCE: {
    role: 'QC Lead',
    description: 'Compliance matrix validation and RFP requirement adherence check.',
  },
  EXECUTIVE: {
    role: 'CEO',
    description: 'Final executive sign-off before proposal submission.',
  },
};

const reviewStatusColors: Record<ReviewStatus, string> = {
  PENDING: 'default',
  IN_PROGRESS: 'processing',
  APPROVED: 'success',
  REJECTED: 'error',
  REWORK: 'warning',
};

const reviewStatusLabels: Record<ReviewStatus, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  REWORK: 'Rework Required',
};

const getReviewStepStatus = (status: ReviewStatus): 'finish' | 'process' | 'wait' | 'error' => {
  switch (status) {
    case 'APPROVED': return 'finish';
    case 'IN_PROGRESS': return 'process';
    case 'REJECTED':
    case 'REWORK': return 'error';
    default: return 'wait';
  }
};

const getReviewStepIcon = (status: ReviewStatus) => {
  switch (status) {
    case 'APPROVED': return <CheckCircleOutlined />;
    case 'IN_PROGRESS': return <LoadingOutlined />;
    case 'REJECTED': return <CloseCircleOutlined />;
    case 'REWORK': return <ExclamationCircleOutlined />;
    default: return <ClockCircleOutlined />;
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return '';
  return new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (fileType?: string) => {
  if (!fileType) return <FileOutlined />;
  if (fileType.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
  if (fileType.includes('word') || fileType.includes('document'))
    return <FileWordOutlined style={{ color: '#1890ff' }} />;
  if (fileType.includes('excel') || fileType.includes('spreadsheet'))
    return <FileExcelOutlined style={{ color: '#52c41a' }} />;
  if (fileType.includes('image'))
    return <FileImageOutlined style={{ color: '#722ed1' }} />;
  return <FileOutlined />;
};

type ModalMode = 'changeOwner' | 'approve' | 'reject';

const ReviewSubGates: React.FC<ReviewSubGatesProps> = ({
  reviewGates,
  opportunityId,
  onGatesUpdated,
  onRollback,
}) => {
  const { hasAnyRole } = useAuth();
  const [modalMode, setModalMode] = useState<ModalMode>('changeOwner');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedGate, setSelectedGate] = useState<ReviewGateDto | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [initializing, setInitializing] = useState(false);
  const [showAllReviewers, setShowAllReviewers] = useState(true);

  // ── Phase D documents state ───────────────────────────────────────────────
  const navigate = useNavigate();
  const [phaseDDocs, setPhaseDDocs] = useState<DocumentDto[]>([]);
  const [loadingPhaseDDocs, setLoadingPhaseDDocs] = useState(false);

  useEffect(() => {
    const fetchPhaseDDocs = async () => {
      setLoadingPhaseDDocs(true);
      try {
        const docs = await documentApi.getByPhase(opportunityId, 'PHASE_D');
        setPhaseDDocs(docs);
      } catch {
        setPhaseDDocs([]);
      } finally {
        setLoadingPhaseDDocs(false);
      }
    };
    fetchPhaseDDocs();
  }, [opportunityId]);

  const phaseDColumns = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (name: string, record: DocumentDto) => (
        <Space size={4}>
          {getFileIcon(record.fileType)}
          <Text style={{ fontSize: 12 }}>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (v: string) => v ? <Tag style={{ fontSize: 11 }}>{v.replace(/_/g, ' ')}</Tag> : '—',
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 140,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v || '—'}</Text>,
    },
    {
      title: 'Size',
      dataIndex: 'fileSizeBytes',
      key: 'size',
      width: 90,
      render: (v: number) => <Text type="secondary" style={{ fontSize: 11 }}>{formatFileSize(v)}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {v ? dayjs(v).format('MMM D, YYYY h:mm A') : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: DocumentDto) => (
        <Button
          type="link"
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => documentApi.downloadFile(record.id, record.fileName)}
          style={{ fontSize: 11, padding: '0 4px' }}
        >
          Download
        </Button>
      ),
    },
  ];

  const isAdmin = hasAnyRole(['ADMIN', 'CEO']);
  const canInitialize = hasAnyRole(['CLOUD_ARCHITECT', 'ENTERPRISE_ARCHITECT', 'ADMIN', 'CEO']);
  const canRollback = hasAnyRole(['BD_MANAGER', 'ADMIN', 'CEO']);

  // ── Rollback state ──────────────────────────────────────────────────────
  const [rollbackModalVisible, setRollbackModalVisible] = useState(false);
  const [rollbackTargetPhase, setRollbackTargetPhase] = useState<string>('PHASE_B');
  const [rollbackReason, setRollbackReason] = useState('');
  const [rollingBack, setRollingBack] = useState(false);

  const handleRollback = async () => {
    if (!rollbackReason.trim()) {
      message.warning('Please provide a reason for rolling back');
      return;
    }
    setRollingBack(true);
    try {
      await workflowApi.rollbackWorkflow(opportunityId, rollbackTargetPhase as any, rollbackReason.trim());
      message.success('Workflow rolled back — gates cleared. Re-initialize when ready.');
      setRollbackModalVisible(false);
      setRollbackReason('');
      onRollback?.();
      onGatesUpdated?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to roll back workflow');
    } finally {
      setRollingBack(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await userApi.getAll({ size: 100, isActive: true });
      setUsers(response.content || []);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (modalVisible && modalMode === 'changeOwner') {
      fetchUsers();
    }
  }, [modalVisible, modalMode, fetchUsers]);

  /** Role-based permission check — no UUID comparison needed. */
  const canActOnGate = (gate: ReviewGateDto): boolean => {
    const allowedRoles = GATE_ROLE_PERMISSIONS[gate.gateType] || ['ADMIN', 'CEO'];
    return hasAnyRole(allowedRoles);
  };

  /**
   * E.1 / E.2 / E.3 are fully async — no blocking.
   * Only E.4 (EXECUTIVE, order=4) must wait for ALL three parallel gates to be approved.
   */
  const getBlockingGates = (gate: ReviewGateDto, sortedGates: ReviewGateDto[]): ReviewGateDto[] => {
    if (gate.gateOrder < 4) return []; // parallel — never blocked
    return sortedGates.filter(
      (other) => other.gateOrder < 4 && other.status !== 'APPROVED',
    );
  };

  const openModal = (mode: ModalMode, gate: ReviewGateDto) => {
    setModalMode(mode);
    setSelectedGate(gate);
    setSelectedUserId(undefined);
    setFeedbackText('');
    setFileList([]);
    setShowAllReviewers(false);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedGate(null);
    setSelectedUserId(undefined);
    setFeedbackText('');
    setFileList([]);
  };

  // ─── Upload props (no auto-upload — collect files for submission) ─────────

  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      setFileList([file as unknown as UploadFile]);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
    maxCount: 1,
    accept: '.pdf,.doc,.docx,.xlsx,.pptx,.txt,.png,.jpg,.jpeg',
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      await reviewApi.initializeGates(opportunityId);
      message.success('Review gates initialized — reviewers assigned automatically');
      onGatesUpdated?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to initialize review gates');
    } finally {
      setInitializing(false);
    }
  };

  const handleOwnershipChange = async () => {
    if (!selectedGate || !selectedUserId) {
      message.warning('Please select a new reviewer');
      return;
    }
    setSubmitting(true);
    try {
      await reviewApi.changeGateOwner(opportunityId, selectedGate.id, selectedUserId);
      message.success('Reviewer assigned successfully');
      closeModal();
      onGatesUpdated?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to change reviewer');
    } finally {
      setSubmitting(false);
    }
  };

  /** Upload any attached file to Phase E document store, then approve. */
  const handleApprove = async () => {
    if (!selectedGate) return;
    setSubmitting(true);
    try {
      // Upload evidence document if attached
      if (fileList.length > 0) {
        const file = (fileList[0] as any).originFileObj || fileList[0];
        await documentApi.uploadFile(file, opportunityId, 'REVIEW_EVIDENCE', 'PHASE_E');
      }
      await reviewApi.approveGate(opportunityId, selectedGate.id, feedbackText.trim() || undefined);
      message.success('Gate approved');
      closeModal();
      onGatesUpdated?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to approve gate');
    } finally {
      setSubmitting(false);
    }
  };

  /** Upload any attached file to Phase E document store, then reject. */
  const handleReject = async () => {
    if (!selectedGate) return;
    if (!feedbackText.trim()) {
      message.warning('Feedback is required when rejecting');
      return;
    }
    setSubmitting(true);
    try {
      if (fileList.length > 0) {
        const file = (fileList[0] as any).originFileObj || fileList[0];
        await documentApi.uploadFile(file, opportunityId, 'REVIEW_EVIDENCE', 'PHASE_E');
      }
      await reviewApi.rejectGate(opportunityId, selectedGate.id, feedbackText.trim());
      message.success('Gate rejected');
      closeModal();
      onGatesUpdated?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to reject gate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalOk = () => {
    if (modalMode === 'changeOwner') handleOwnershipChange();
    else if (modalMode === 'approve') handleApprove();
    else if (modalMode === 'reject') handleReject();
  };

  // ─── Not yet initialized ──────────────────────────────────────────────────

  if (!reviewGates || reviewGates.length === 0) {
    return (
      <Card
        size="small"
        style={{ marginTop: 8, borderColor: '#1677ff' }}
        title={
          <Space>
            <TeamOutlined style={{ color: '#1677ff' }} />
            <Text strong style={{ fontSize: 14 }}>Phase E: Review Gates</Text>
          </Space>
        }
      >
        <Alert
          message="Review gates are being initialized..."
          description="E.1 Technical, E.2 Strategy & Delivery, and E.3 Compliance reviews will run in parallel. Reviewers are auto-assigned from the RACI matrix and notified automatically. Please refresh if gates do not appear shortly."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Review responsibilities (RACI):
        </Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(GATE_DESCRIPTIONS).map(([gateType, info]) => (
            <div
              key={gateType}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '6px 10px',
                background: 'var(--color-bg-hover)',
                borderRadius: 6,
                border: '1px solid var(--color-border)',
              }}
            >
              <Tag color="blue" style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }}>
                {GATE_TYPE_LABELS[gateType]}
              </Tag>
              <div>
                <Text strong style={{ fontSize: 12 }}>{info.role}</Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  {info.description}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  // ─── Gates initialized — show steps ──────────────────────────────────────

  // E.4 (CEO Executive) is handled in Phase F (PipelineSummary) — only show E.1-E.3 here
  const sortedGates = [...reviewGates]
    .filter(g => g.gateOrder < 4)
    .sort((a, b) => a.gateOrder - b.gateOrder);
  const allParallelDone = sortedGates.every(g => g.status === 'APPROVED');
  const currentSubIndex = -1; // parallel gates — no sequential index needed

  const items = sortedGates.map((gate) => {
    const blockingGates = getBlockingGates(gate, sortedGates);
    const isBlocked = blockingGates.length > 0;
    const canAct = canActOnGate(gate);
    const isInProgress = gate.status === 'IN_PROGRESS';
    const isParallel = gate.gateOrder < 4; // E.1 / E.2 / E.3

    const reviewerRoleLabel = gate.reviewerRole
      ? (ROLE_LABELS[gate.reviewerRole] || gate.reviewerRole)
      : null;
    const expectedRole = GATE_DESCRIPTIONS[gate.gateType]?.role;

    return {
      title: (
        <Space>
          <Text strong style={{ fontSize: 13 }}>
            {GATE_TYPE_LABELS[gate.gateType] || gate.gateType}
          </Text>
          <Tag color={reviewStatusColors[gate.status]}>
            {reviewStatusLabels[gate.status] || gate.status}
          </Tag>
          {isParallel && (
            <Tag color="geekblue" style={{ fontSize: 10 }}>Parallel</Tag>
          )}
          {!isParallel && (
            <Tag color="purple" style={{ fontSize: 10 }}>Final Sign-off</Tag>
          )}
        </Space>
      ),
      description: (
        <div style={{ fontSize: 12 }}>
          {/* Reviewer info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
            <UserOutlined style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }} />
            {gate.reviewer ? (
              <>
                <Text style={{ fontSize: 12, ...(gate.reviewerActive === false ? { color: 'var(--color-text-tertiary)', textDecoration: 'line-through' } : {}) }}>
                  {gate.reviewer}
                </Text>
                {gate.reviewerActive === false && (
                  <Tag color="default" style={{ fontSize: 10, margin: 0 }}>Deactivated</Tag>
                )}
                {reviewerRoleLabel && (
                  <Tag style={{ fontSize: 10, margin: 0 }}>{reviewerRoleLabel}</Tag>
                )}
              </>
            ) : (
              <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                Unassigned — expected: <strong>{expectedRole}</strong>
              </Text>
            )}
            {gate.startedAt && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                | Started: {formatDateTime(gate.startedAt)}
              </Text>
            )}
            {gate.completedAt && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                | Completed: {formatDateTime(gate.completedAt)}
              </Text>
            )}
          </div>

          {/* Feedback inline display */}
          {gate.feedback && (
            <div
              style={{
                marginTop: 4,
                padding: '6px 10px',
                background: gate.status === 'APPROVED' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                borderRadius: 4,
                borderLeft: `3px solid ${gate.status === 'APPROVED' ? '#52c41a' : '#faad14'}`,
              }}
            >
              <Text type="secondary" style={{ fontSize: 11 }}>
                {gate.status === 'APPROVED' ? 'Approval note: ' : 'Rejection reason: '}
              </Text>
              <Text style={{ fontSize: 12 }}>{gate.feedback}</Text>
            </div>
          )}

          {/* CEO gate: show which parallel reviews are still pending */}
          {isBlocked && (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <LockOutlined style={{ fontSize: 11, color: '#faad14' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Waiting for all parallel reviews to be approved:
                </Text>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {blockingGates.map((bg) => (
                  <Tag key={bg.id} color={reviewStatusColors[bg.status]} style={{ fontSize: 10 }}>
                    {GATE_TYPE_LABELS[bg.gateType]} — {reviewStatusLabels[bg.status]}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Active — can approve/reject */}
            {isInProgress && canAct && !isBlocked && (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={(e) => { e.stopPropagation(); openModal('approve', gate); }}
                  style={{ fontSize: 11 }}
                >
                  Approve
                </Button>
                <Button
                  danger
                  size="small"
                  icon={<StopOutlined />}
                  onClick={(e) => { e.stopPropagation(); openModal('reject', gate); }}
                  style={{ fontSize: 11 }}
                >
                  Reject
                </Button>
              </>
            )}

            {/* Wrong role for this gate */}
            {isInProgress && !canAct && !isBlocked && (
              <Tooltip title={`Only ${expectedRole}, Admin, or CEO can act on this gate`}>
                <Button size="small" icon={<LockOutlined />} disabled style={{ fontSize: 11 }}>
                  {expectedRole || 'Reviewer'} only
                </Button>
              </Tooltip>
            )}

            {/* CEO gate: blocked because parallel reviews not done */}
            {isInProgress && isBlocked && (
              <Tooltip title="All parallel reviews (E.1, E.2, E.3) must be approved before CEO sign-off">
                <Button size="small" icon={<LockOutlined />} disabled style={{ fontSize: 11 }}>
                  Awaiting parallel reviews
                </Button>
              </Tooltip>
            )}

            {/* Assign/Reassign reviewer */}
            {(isAdmin || !gate.reviewer) && gate.status !== 'APPROVED' && (
              <Tooltip title={gate.reviewer ? 'Reassign reviewer' : 'Assign reviewer'}>
                <Button
                  type="link"
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={(e) => { e.stopPropagation(); openModal('changeOwner', gate); }}
                  style={{ fontSize: 11, padding: '0 4px', height: 'auto', color: '#1677ff' }}
                >
                  {gate.reviewer ? 'Reassign' : 'Assign Reviewer'}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      ),
      status: getReviewStepStatus(gate.status),
      icon: getReviewStepIcon(gate.status),
    };
  });

  // ─── Modal config ─────────────────────────────────────────────────────────

  const modalTitle =
    modalMode === 'approve' ? 'Approve Gate'
      : modalMode === 'reject' ? 'Reject Gate'
      : 'Assign / Reassign Reviewer';

  const modalOkText =
    modalMode === 'approve' ? 'Approve'
      : modalMode === 'reject' ? 'Reject'
      : 'Save';

  const modalOkDisabled =
    modalMode === 'changeOwner' ? !selectedUserId
      : modalMode === 'reject' ? !feedbackText.trim()
      : false;

  // ── Derive flow state for the banner ──────────────────────────────────────
  const gateStatuses = Object.fromEntries(sortedGates.map((g) => [g.gateType, g.status]));

  const parallelGateTypes = ['TECHNICAL', 'STRATEGY_DELIVERY', 'COMPLIANCE'] as const;
  const parallelColors: Record<string, string> = {
    TECHNICAL: 'geekblue',
    STRATEGY_DELIVERY: 'cyan',
    COMPLIANCE: 'orange',
  };
  const parallelLabels: Record<string, string> = {
    TECHNICAL: 'E.1 Technical',
    STRATEGY_DELIVERY: 'E.2 Strategy',
    COMPLIANCE: 'E.3 Compliance',
  };

  return (
    <div
      style={{
        background: 'var(--color-bg-hover)',
        borderRadius: 8,
        padding: '16px 20px',
        border: '1px solid var(--color-border)',
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
        Phase E: Review Sub-Gates
      </Text>

      {/* ── Sequential flow diagram ─────────────────────────────── */}
      <div
        style={{
          background: 'var(--color-bg-container)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 16,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
          Review flow
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Parallel group */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: '6px 10px',
              background: 'rgba(59, 130, 246, 0.12)',
              border: '1px dashed #3B82F6',
              borderRadius: 6,
            }}
          >
            <Text style={{ fontSize: 10, color: '#6B7280', marginBottom: 2 }}>Run simultaneously</Text>
            {parallelGateTypes.map((gt) => {
              const status = gateStatuses[gt];
              const approved = status === 'APPROVED';
              return (
                <div key={gt} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag
                    color={approved ? 'success' : parallelColors[gt]}
                    style={{ fontSize: 10, margin: 0 }}
                    icon={approved ? <CheckCircleOutlined /> : undefined}
                  >
                    {parallelLabels[gt]}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {status === 'APPROVED' ? '✓ Approved' : status === 'IN_PROGRESS' ? 'In Review' : status === 'REJECTED' ? '✗ Rejected' : 'Pending'}
                  </Text>
                </div>
              );
            })}
          </div>

          {/* Arrow to Phase F CEO sign-off */}
          {allParallelDone && (
            <>
              <ArrowRightOutlined style={{ fontSize: 14, color: '#52c41a', flexShrink: 0 }} />
              <Tag color="success" style={{ fontSize: 10 }}>
                CEO sign-off moves to Phase F
              </Tag>
            </>
          )}
        </div>
      </div>

      {/* ── Phase D Review Context — unified tabbed section ─────────── */}
      <Card
        size="small"
        style={{ marginBottom: 16, borderColor: '#d9e8ff' }}
        title={
          <Space>
            <FolderOpenOutlined style={{ color: '#1677ff' }} />
            <Text strong style={{ fontSize: 13 }}>Phase D Review Context</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Reference materials from the Technical Writer&apos;s work
            </Text>
          </Space>
        }
        extra={
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={() => navigate('/content-library')}
            style={{ fontSize: 12 }}
          >
            View in Content Library
          </Button>
        }
      >
        <Tabs
          defaultActiveKey="phase-d-docs"
          size="small"
          items={[
            {
              key: 'phase-d-docs',
              label: (
                <Space size={4}>
                  <FileTextOutlined />
                  <span>Phase D Documents</span>
                  {phaseDDocs.length > 0 && (
                    <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                      {phaseDDocs.length}
                    </Tag>
                  )}
                </Space>
              ),
              children: loadingPhaseDDocs ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <Spin size="small" />
                  <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                    Loading Phase D documents...
                  </Text>
                </div>
              ) : phaseDDocs.length === 0 ? (
                <Alert
                  message="No Phase D documents found"
                  description="The Technical Writer has not uploaded any documents for Phase D yet. Documents will appear here once uploaded."
                  type="info"
                  showIcon
                />
              ) : (
                <Table
                  dataSource={phaseDDocs}
                  columns={phaseDColumns}
                  rowKey="id"
                  size="small"
                  pagination={phaseDDocs.length > 8 ? { pageSize: 8, size: 'small' } : false}
                />
              ),
            },
            {
              key: 'compliance-matrix',
              label: (
                <Space size={4}>
                  <SafetyCertificateOutlined />
                  <span>Compliance Matrix</span>
                </Space>
              ),
              children: (
                <div style={{ marginTop: -12 }}>
                  <ComplianceMatrixForm opportunityId={opportunityId} onComplete={() => {}} />
                </div>
              ),
            },
            {
              key: 'ai-analysis',
              label: (
                <Space size={4}>
                  <ExperimentOutlined />
                  <span>AI Analysis</span>
                </Space>
              ),
              children: (
                <ProposalAIReview opportunityId={opportunityId} />
              ),
            },
          ]}
        />
      </Card>

      <Steps
        direction="vertical"
        size="small"
        current={currentSubIndex >= 0 ? currentSubIndex : sortedGates.length}
        items={items}
      />

      {/* ── Rollback panel: shown when any gate is REJECTED ──────── */}
      {canRollback && sortedGates.some((g) => g.status === 'REJECTED') && (
        <div
          style={{
            marginTop: 16,
            padding: '12px 16px',
            background: 'rgba(245, 158, 11, 0.12)',
            border: '1px solid #92400E',
            borderRadius: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <RollbackOutlined style={{ fontSize: 16, color: '#D97706', marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', fontSize: 13, color: '#92400E' }}>
                One or more review gates have been rejected
              </Text>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                The proposal needs to be reworked. You can roll back the workflow to a previous phase,
                address the feedback, and re-submit through the review gates.
              </Text>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Text style={{ fontSize: 12 }}>Rejected gates:</Text>
                {sortedGates
                  .filter((g) => g.status === 'REJECTED')
                  .map((g) => (
                    <Tag key={g.id} color="error" style={{ fontSize: 11 }}>
                      {GATE_TYPE_LABELS[g.gateType]} — {g.feedback || 'No reason given'}
                    </Tag>
                  ))}
              </div>
              <Button
                type="default"
                danger
                icon={<RollbackOutlined />}
                size="small"
                style={{ marginTop: 10 }}
                onClick={() => setRollbackModalVisible(true)}
              >
                Reopen & Roll Back for Rework
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rollback modal ────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <RollbackOutlined style={{ color: '#D97706' }} />
            <span>Reopen Workflow for Rework</span>
          </Space>
        }
        open={rollbackModalVisible}
        onCancel={() => { setRollbackModalVisible(false); setRollbackReason(''); }}
        onOk={handleRollback}
        okText="Roll Back & Reopen"
        okButtonProps={{ danger: true, disabled: !rollbackReason.trim() }}
        confirmLoading={rollingBack}
        destroyOnClose
        width={520}
      >
        <Alert
          message="This will reset Phase E review gates and move the workflow back to the selected phase."
          description="All current review gates will be cleared. Once the rework is complete, re-advance to Phase E and re-initialize the gates."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 12 }}>
          <Text style={{ display: 'block', marginBottom: 6 }}>Roll back to:</Text>
          <Select
            value={rollbackTargetPhase}
            onChange={(v) => setRollbackTargetPhase(v)}
            style={{ width: '100%' }}
            options={[
              { value: 'PHASE_B', label: 'Gate B — Feasibility (redo feasibility studies)' },
              { value: 'PHASE_C', label: 'Gate C — Proposal Planning (redo kickoff plan)' },
              { value: 'PHASE_D', label: 'Gate D — Compliance & Content (redo compliance matrix / proposal)' },
            ]}
          />
        </div>

        <div>
          <Text style={{ display: 'block', marginBottom: 6 }}>
            Reason for rollback <span style={{ color: '#ff4d4f' }}>*</span>
          </Text>
          <TextArea
            value={rollbackReason}
            onChange={(e) => setRollbackReason(e.target.value)}
            placeholder="Summarise what needs to be reworked and why the reviews were rejected..."
            rows={4}
            showCount
            maxLength={500}
          />
        </div>
      </Modal>

      {/* ── Gate action modal ─────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            {modalMode === 'approve' && <CheckOutlined style={{ color: '#52c41a' }} />}
            {modalMode === 'reject' && <StopOutlined style={{ color: '#ff4d4f' }} />}
            {modalMode === 'changeOwner' && <SwapOutlined />}
            <span>{modalTitle}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={closeModal}
        onOk={handleModalOk}
        okText={modalOkText}
        okButtonProps={{ danger: modalMode === 'reject', disabled: modalOkDisabled }}
        confirmLoading={submitting}
        destroyOnClose
        width={560}
      >
        {/* Gate summary */}
        {selectedGate && (
          <Descriptions size="small" column={2} style={{ marginBottom: 16 }} bordered>
            <Descriptions.Item label="Gate" span={2}>
              <Text strong>{GATE_TYPE_LABELS[selectedGate.gateType]}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Responsible">
              <Text>{GATE_DESCRIPTIONS[selectedGate.gateType]?.role || '—'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Reviewer">
              <Text>{selectedGate.reviewer || 'Unassigned'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status" span={2}>
              <Tag color={reviewStatusColors[selectedGate.status]}>
                {reviewStatusLabels[selectedGate.status]}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* Approve / Reject form */}
        {(modalMode === 'approve' || modalMode === 'reject') && (
          <div>
            <Text style={{ display: 'block', marginBottom: 6 }}>
              Feedback
              {modalMode === 'reject' && <span style={{ color: '#ff4d4f' }}> * (required)</span>}
              {modalMode === 'approve' && (
                <Text type="secondary" style={{ fontSize: 12 }}> (optional)</Text>
              )}
            </Text>
            <TextArea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={
                modalMode === 'approve'
                  ? 'Optional: add approval notes or comments...'
                  : 'Required: explain the reason for rejection and what needs to be corrected...'
              }
              rows={4}
              showCount
              maxLength={1000}
              style={{ marginBottom: 12 }}
            />

            {/* Document attachment */}
            <Text style={{ display: 'block', marginBottom: 6 }}>
              <PaperClipOutlined /> Attach Supporting Document
              <Text type="secondary" style={{ fontSize: 12 }}> (optional)</Text>
            </Text>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} size="small">
                Select File
              </Button>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                PDF, Word, Excel, PowerPoint, image — max 1 file
              </Text>
            </Upload>

            {modalMode === 'reject' && (
              <Alert
                message="A rejection reason is required and will be logged for the audit trail."
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
          </div>
        )}

        {/* Change / Assign reviewer */}
        {modalMode === 'changeOwner' && (
          <div>
            <Text style={{ display: 'block', marginBottom: 8 }}>
              Select Reviewer:
            </Text>
            {(() => {
              // The primary (non-admin) role for this gate
              const gateExpectedRole = selectedGate
                ? (GATE_ROLE_PERMISSIONS[selectedGate.gateType] ?? []).find(
                    (r) => r !== 'ADMIN' && r !== 'CEO'
                  )
                : undefined;
              const filteredUsers = users.filter((u) => {
                if (u.id === selectedGate?.reviewerId) return false;
                if (!showAllReviewers && gateExpectedRole) return u.role === gateExpectedRole;
                return true;
              });
              return (
                <>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Search and select a user..."
                    showSearch
                    optionFilterProp="label"
                    loading={loadingUsers}
                    value={selectedUserId}
                    onChange={(value) => setSelectedUserId(value)}
                    options={filteredUsers.map((u) => ({
                      value: u.id,
                      label: `${u.fullName} (${ROLE_LABELS[u.role] || u.role})`,
                    }))}
                  />
                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {showAllReviewers
                        ? 'Showing all active users'
                        : `Showing ${gateExpectedRole ? (ROLE_LABELS[gateExpectedRole] || gateExpectedRole) : 'role-matched'} only`}
                    </Text>
                    {' — '}
                    <Button
                      type="link"
                      size="small"
                      style={{ fontSize: 12, padding: 0 }}
                      onClick={() => setShowAllReviewers(!showAllReviewers)}
                    >
                      {showAllReviewers ? 'Filter by role' : 'Show all users'}
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReviewSubGates;
